﻿import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { safeParseInt } from "@lib/TypeUtil"
import { Attachment } from "@model/Attachment"
import {
    labelsForServiceItemRequiredFields,
    missingServiceItemRequiredFields,
} from "@model/company/WorkOrderRequiredFieldsUtil"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { Step } from "@model/Step"
import { PermissionObject, PermissionType } from "@model/user/User"
import { WorkOrderStatus } from "@model/workOrders/WorkOrder"
import { WorkRequestStatus, WorkRequestType } from "@model/workRequests/WorkRequest"
import { CompanyBloc, CompanyState } from "@state/company/CompanyBloc"
import { StatusBloc, StatusState } from "@state/StatusBloc"
import { UserBloc, UserState } from "@state/user/UserBloc"
import { CreateNewWorkOrderScreenBloc } from "@state/workOrders/CreateNewWorkOrderModalBloc"
import { ServiceItemBloc } from "@state/workRequests/ServiceItemBloc"

type Dependencies = [ServiceItemForm, UserState, CompanyState, StatusState]
type StateSelection = ServiceItemForm & { serviceScheduleStepsInUse: Step[] }

export class ServiceItemViewModel extends BlocCoordinator<Dependencies, StateSelection> {
    private onChange?: (form: ServiceItemForm) => void

    constructor(
        private bloc: ServiceItemBloc,
        private userBloc: UserBloc,
        private companyBloc: CompanyBloc,
        private statusBloc: StatusBloc,
        private createNewWorkOrderScreenBloc: CreateNewWorkOrderScreenBloc
    ) {
        super([bloc, userBloc, companyBloc, statusBloc])
    }

    protected transform = ([form, userState, companyState, statusState]: Dependencies): StateSelection => ({
        ...form,
        contacts: companyState.contacts,
        serviceCodes: Object.values(companyState.serviceCodes),
        mechanics: companyState.settings.mechanics,
        serviceScheduleStepsInUse: companyState.serviceScheduleStepsInUse,
    })

    getUser = () => this.userBloc.state.user

    updateField = <K extends keyof ServiceItemForm>(field: K, value: ServiceItemForm[K]) => {
        this.bloc.updateField(field, value)
        this.onChange?.(this.getForm())
    }

    /**
     * Completing a Service Item has to clear the company's required-field selection first. On a miss
     * the item stays where it is and the user is told exactly which fields are outstanding, rather
     * than being left with a button that quietly does nothing.
     */
    onCompleteClicked = () => {
        const missing = missingServiceItemRequiredFields(
            this.getForm(),
            this.companyBloc.state.settings.workOrderRequiredFields.serviceItemFields
        )

        if (missing.length > 0) {
            this.statusBloc.enqueueErrorMessage(
                `Cannot complete this Service Item. Required: ${labelsForServiceItemRequiredFields(missing).join(", ")}`
            )
            return
        }

        this.updateField("status", WorkRequestStatus.Done)
        this.updateField("isSaved", true)
    }

    onReopenClicked = () => {
        this.updateField("status", WorkRequestStatus.ToDo)
        this.updateField("isSaved", true)
    }

    stepChanged = (value: string) => {
        this.bloc.stepChanged(safeParseInt(value), this.companyBloc.state.serviceScheduleStepsInUse)
        this.onChange?.(this.getForm())
    }

    attachmentsChanged = async (files: File[], formData: FormData) => {
        const message = this.statusBloc.enqueueInfoMessage("Attaching File...")
        const outcome = await this.bloc.attachmentsChanged(
            files,
            formData,
            this.userBloc.state.user.id,
            this.userBloc.state.user.companyId
        )
        this.statusBloc.hideInfoMessage(message)
        this.onChange?.(this.getForm())
        return outcome
    }

    attachmentDeleted = (attachment: Attachment) => {
        this.bloc.attachmentsDelete(attachment.name, this.userBloc.state.user.id, this.userBloc.state.user.companyId)
        this.onChange?.(this.getForm())
    }

    rotateImage = (attachment: Attachment, angle: number) =>
        this.bloc.rotateImage(attachment.name, angle, this.userBloc.state.user.id, this.userBloc.state.user.companyId)

    clearAttachmentsOnCancel = () => {
        this.bloc.clearAttachmentsOnCancel()
        this.onChange?.(this.getForm())
    }

    attachmentError = (errors: string[]) => this.statusBloc.enqueueErrorMessage(errors.join("<br>"))

    getForm(): ServiceItemForm {
        return { ...this.state }
    }

    setOnChangeListener(listener: (form: ServiceItemForm) => void) {
        this.onChange = listener
    }

    loadServiceItemHistory = () => {
        if (this.state.id) {
            this.bloc.fetchHistory(this.state.id)
        }
    }

    canDeclineWorkRequest = (): boolean => {
        const isEditinsAServiceItemAlreadyAssigned = (this.state.workOrderId ?? 0) > 0
        /*const hasMultipleRequests = Object.keys(this.createNewWorkOrderScreenBloc.state.workRequests).length > 1*/
        const hasDeletePermission = this.hasWorkRequestDeletePermission()
        const workOrderNotClosed = this.createNewWorkOrderScreenBloc.state.workOrder?.status !== WorkOrderStatus.Closed

        return hasDeletePermission && workOrderNotClosed && isEditinsAServiceItemAlreadyAssigned
    }

    canDeleteWorkRequest = (): boolean => {
        /*const hasMultipleRequests = Object.keys(this.createNewWorkOrderScreenBloc.state.workRequests).length > 1*/
        const isNewServiceItemNotSaved = (this.state.workOrderId ?? 0) <= 0

        const isServiceType = this.state.workRequestType === WorkRequestType.Service
        const hasDeletePermission = this.hasWorkRequestDeletePermission()
        const isNotDone = this.state.status !== WorkRequestStatus.Done
        const workOrderNotClosed = this.createNewWorkOrderScreenBloc.state.workOrder?.status !== WorkOrderStatus.Closed

        return isNewServiceItemNotSaved || (isServiceType && hasDeletePermission && isNotDone && workOrderNotClosed)
    }

    hasWorkRequestDeletePermission = () =>
        this.userBloc.state.user.hasAccess(PermissionObject.WorkRequest, PermissionType.Delete)
}
